'use client';
import { useRouter } from 'next/navigation';

type TabOption = 'grid' | 'list' | 'detail' | 'video';

type SidebarTabsProps = {
  setActiveTab: (tab: TabOption) => void;
  activeTab: TabOption;
  projectName?: string;
  position: string;
  currentSlide?: number;
  setCurrentSlide?: (index: number) => void;
  totalSlides?: number;
  videoControlsEnabled: boolean;
};

export default function SidebarTabs({
  setActiveTab,
  activeTab,
  projectName,
  position,
  currentSlide,
  totalSlides,
  videoControlsEnabled,
}: SidebarTabsProps) {
  const router = useRouter();
  const list = activeTab === 'list';

  // Always render all tabs for consistent layout
  const tabs: TabOption[] = ['grid', 'list', 'detail', 'video'];

  return (
    <div className={`menu-box top-5 ${position} flex flex-col justify-between h-full`}>
      {/* Top content */}
      <div className="flex gap-10 px-4 md:px-0 border-b md:border-0 border-[#2B332A]/40 py-[13px] md:py-0 justify-between md:justify-start">
        <div>
          {list ? (
            <button onClick={() => router.push('/projects')} className="font-light">
              Back
            </button>
          ) : (
            <button onClick={() => setActiveTab('list')} className="font-light">
              Back
            </button>
          )}
        </div>
        <div>
          <ul className="flex flex-row md:flex-col gap-2 md:gap-0 category-titles">
            {tabs.map((tab) => {
              const isVideoTab = tab === 'video';

              // Hide video tab text and disable pointer when not enabled
              const hiddenStyle =
                !videoControlsEnabled && isVideoTab ? 'hidden pointer-events-none select-none' : '';

              // Assign specific class based on tab
              const tabClass =
                tab === 'grid'
                  ? 'gallery-view'
                  : tab === 'list'
                    ? 'listing-view'
                    : tab === 'detail'
                      ? 'project-details'
                      : 'project-video';

              return (
                <li
                  key={tab}
                  onClick={() => {
                    if (videoControlsEnabled || !isVideoTab) {
                      setActiveTab(tab);
                    }
                  }}
                  className={`cursor-pointer font-light py-0 transition-all duration-200 ${tabClass} ${
                    activeTab === tab ? 'text-[#9E703E]' : 'hover:text-[#9E703E]'
                  } ${hiddenStyle}`}
                >
                  {tab === 'grid'
                    ? 'Gallery View'
                    : tab === 'list'
                      ? 'Listing View'
                      : tab === 'detail'
                        ? 'Project Detail'
                        : 'Project Video'}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Project Name */}
      {projectName && (
        <h1 className="text-[22px] font-light px-4 md:px-0 leading-none mt-16 lg:mt-0">
          {projectName}
        </h1>
      )}

      {/* Slide Counter */}
      <div className="px-4 lg:px-0 pb-5 hidden lg:block counter-num">
        {typeof currentSlide === 'number' && typeof totalSlides === 'number' && (
          <div className="text-base leading-none font-light max-w-[81px] flex items-center justify-between">
            <span>{currentSlide}</span> / <span>{totalSlides}</span>
          </div>
        )}
      </div>
    </div>
  );
}
